import React from "react";
import * as ReactDOM from "react-dom";
import {
  Grid,
  GridColumn as Column,
  GridNoRecords,
} from "@progress/kendo-react-grid";
import "@progress/kendo-theme-material/dist/all.css";

import { useDispatch, useSelector } from "react-redux";
import { RegistrationStatus } from "../../../common/enum/index";
import i18next, { t } from "i18next";
import { useTranslation } from "react-i18next";
import {
  setSnackbarMessage,
  setSnackbarOpen,
  setSnackbarSeverity,
  setConfirmationDialog,
} from "../../AdminView/adminSlice";
import { debounce } from "lodash";

import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import { Stack, Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { updateManagerInvite } from "../../OnBoarding/slice";

import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  getManagerInviteList,
  setManagerInviteLimit,
  setManagerInviteOffset,
  setManagerRevokedPayload,
} from "../../../redux/features/authSlice";
import { enqueueSnackbar, useSnackbar } from "notistack";

import { IntlProvider, load, LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import esMessages from '../../Donations/DonationCard/tlklang.json';

loadMessages(esMessages, 'pt-PT');

const LoadingPanel = () => {
  const loadingPanel = (
    <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image" />
      <div className="k-loading-color" />
    </div>
  );
  const gridContent = document && document.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanel, gridContent)
    : loadingPanel;
};

const StatusCell = (props) => {
  const { translate } = useSelector((state) => state);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  React.useEffect(() => {
    i18next.changeLanguage(translate);
  }, [translate]);

  return <td>{ props.dataItem.status == "REVOKED" ? "Inativo" : t(`${props.dataItem.status}`)}</td>;
};

const ActionCell = (props) => {
  const dispatch = useDispatch();

  const {
    auth: {
      managerInviteOffset,
      managerInviteLimit,
      profile: { entity, status},
    },
  } = useSelector((state) => state);

  const onApproved = React.useCallback(() => {
    const payload = {
      inviteToken: props.dataItem.inviteToken,
      tokenExpiry: props.dataItem.tokenExpiry,
      status: RegistrationStatus.active,
      entityPrimaryId: props.dataItem.entityPrimaryId,
      email: props.dataItem.email,
    };
    dispatch(updateManagerInvite(payload))
      .unwrap()
      .then((res) => {
        let data = {
          limit: managerInviteLimit,
          offset: managerInviteOffset,
          id: entity.id,
        };
        dispatch(getManagerInviteList(data));
        enqueueSnackbar({
          message: t("successfullyReActive"),
          variant: "success",
        });
      })
      .catch((error) => {
        enqueueSnackbar({
          message: t("somethingWentWrong"),
          variant: "error",
        });
      });
  }, []);

  const onRejectedHandle = React.useCallback(() => {
    const payload = {
      inviteToken: props.dataItem.inviteToken,
      tokenExpiry: props.dataItem.tokenExpiry,
      status: RegistrationStatus.revoked,
      entityPrimaryId: props.dataItem.entityPrimaryId,
      email: props.dataItem.email,
    };
    dispatch(setManagerRevokedPayload(payload));
    dispatch(setConfirmationDialog(true));
  }, []);

  return (
    <td>
      <Stack direction={"row"} mt="10px" alignItems="center" spacing={1}>
        {props.dataItem.status === RegistrationStatus.revoked && (
          <Tooltip title={"Ativar"} arrow>
            <Button
              sx={{ p: 0, minWidth: 0 }}
              onClick={onApproved}
              disabled={
                status === RegistrationStatus.inActive ||
                status === RegistrationStatus.inAtivo
              }
            >
              <DoneIcon fontSize="small" />
            </Button>
          </Tooltip>
        )}
        {props.dataItem.status !== RegistrationStatus.revoked && (
          <Tooltip title={t("revoked")} arrow>
            <Button
              sx={{ p: 0, minWidth: 0 }}
              onClick={onRejectedHandle}
              disabled={
                status === RegistrationStatus.inActive ||
                status === RegistrationStatus.inAtivo
              }
            >
              <ClearIcon fontSize="small" />
            </Button>
          </Tooltip>
        )}
      </Stack>
    </td>
  );
};
const ManageManagersTable = ({ t }) => {
  const {
    adminDashboard: { confirmationDialog },
    auth: {
      managerInviteCount,
      managerInviteOffset,
      managerInviteLimit,
      managerInviteEntities,
      managerRevokedPayload,
      profile: { entity, status },
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const handleClose = React.useCallback(() => {
    dispatch(setConfirmationDialog(false));
  }, [dispatch]);

  const onConfirm = () => {
    dispatch(updateManagerInvite(managerRevokedPayload))
      .unwrap()
      .then((res) => {
        let data = {
          limit: managerInviteLimit,
          offset: managerInviteOffset,
          id: entity.id,
        };
        dispatch(getManagerInviteList(data));
        dispatch(setConfirmationDialog(false));
        dispatch(setManagerRevokedPayload({}));
        enqueueSnackbar({
          message: t("successfullyRevoked"),
          variant: "success",
        });
      })
      .catch((error) => {
        enqueueSnackbar({
          message: t("somethingWentWrong"),
          variant: "error",
        });
      });
  };

  const timer = React.useRef(null);
  const [dataState, setDataState] = React.useState({
    take: 20,
    skip: 0,
  });

  React.useEffect(() => {
    const data = {
      limit: dataState.take + dataState.skip,
      offset: dataState.skip,
      id: entity.id,
    };
    dispatch(getManagerInviteList(data));
  }, [dispatch]);

  const debouncedApiCall = debounce(async (value) => {
    const limit = value.skip + value.take;
    const offset = value.skip;
    let data = {
      limit: limit,
      offset: offset,
      id: entity.id,
    };
    dispatch(getManagerInviteList(data));
    dispatch(setManagerInviteLimit(limit));
    dispatch(setManagerInviteOffset(offset));
  }, 1000);

  const dataStateChange = (e) => {
    const value = e.dataState;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      debouncedApiCall(value);
    }, 1000);
    setDataState(e.dataState);
  };

  return (
    <React.Fragment>
      <>
      <LocalizationProvider language="pt-PT" >
        <IntlProvider locale="pt">
        <Grid
        data={managerInviteEntities}
        resizable={true}
        // skip={page.skip}
        // take={page.take}
        total={managerInviteCount}
        pageable={true}
        {...dataState}
        // onPageChange={pageChange}
        onDataStateChange={dataStateChange}
        // groupable={true}
      >
        <GridNoRecords>{t("noRecordAvailable")}</GridNoRecords>
        <Column field="email" title={t("email")} />
        <Column field="status" title={t("status")} cell={StatusCell} />
        <Column field="" title={`Ações`} cell={ActionCell} />
      </Grid>
        </IntlProvider>
      </LocalizationProvider>
      </>
      
      <ConfirmDialog
        open={confirmationDialog}
        handleClose={handleClose}
        title={t("")}
        content={t("confirmRevokeUser")}
        onConfirm={onConfirm}
        cancelButtonTitle={t("cancel")}
        confirmButtonTitle={t("confirm")}
      />
    </React.Fragment>
  );
};

export default ManageManagersTable;
